// §6.3 — the house bar: one slim strip, left-aligned, running apps then a tray and a clock.
import { useEffect, useState } from 'react';
import { LayoutGrid } from '@/assets/icons';
import { COPY } from '@/copy';
import {
  useFocusedWindowId,
  useKernel,
  useNotificationBadges,
  useUnhealthyApps,
  useWindows,
} from '@/kernel/react';

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(timer);
  }, []);
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function Bar() {
  const kernel = useKernel();
  const windows = useWindows();
  const focusedId = useFocusedWindowId();
  const unhealthy = useUnhealthyApps();
  const badges = useNotificationBadges();
  const time = useClock();

  // §5.4 — a Snap Group gets its own bar entry, and it moves all of its members together.
  // Derived from the windows themselves, so it appears and disappears with the group.
  const groups = kernel.snap.groupsOn(kernel.wm.getState().activeDesktopId);

  return (
    <div className="house-bar" data-testid="bar">
      <button
        type="button"
        className="house-bar-launcher"
        aria-label={COPY.chrome.openLauncher}
        data-testid="bar-launcher"
        onClick={() => kernel.shellui.toggleOverlay('launcher')}
      >
        <LayoutGrid size={16} aria-hidden />
      </button>

      <div className="house-bar-groups" data-testid="bar-groups">
        {groups.map((group) => (
          <button
            key={group.groupId}
            type="button"
            className="house-bar-group"
            data-testid={`bar-group-${group.layoutId}`}
            data-count={group.windows.length}
            title={COPY.chrome.snappedWindows(group.windows.length)}
            // §5.4 — hovering the group's bar entry brings the whole group back.
            onPointerEnter={() => kernel.snap.restoreGroup(group.groupId)}
            onFocus={() => kernel.snap.restoreGroup(group.groupId)}
            onClick={() => {
              const anyVisible = group.windows.some((win) => {
                const current = kernel.wm.get(win.id);
                return current !== undefined && current.state !== 'minimized';
              });
              if (anyVisible) kernel.snap.minimizeGroup(group.groupId);
              else kernel.snap.restoreGroup(group.groupId);
            }}
          >
            <span className="house-bar-entry-label">{group.windows.length}</span>
          </button>
        ))}
      </div>

      <div className="house-bar-windows" data-testid="bar-windows">
        {windows.map((win) => (
          <button
            key={win.id}
            type="button"
            className="house-bar-entry"
            data-testid={`bar-entry-${win.appId}`}
            data-window-id={win.id}
            data-active={String(win.id === focusedId && win.state !== 'minimized')}
            // §9.6 — three consecutive misses and the entry dims to 50 %.
            data-health={unhealthy.has(win.appId) ? 'unhealthy' : 'ok'}
            // §13 — the app's unread count. Per *app*, not per window: an app is what posts.
            data-badge={badges.get(win.appId) ?? 0}
            title={win.title}
            onClick={() => {
              const current = kernel.wm.get(win.id);
              if (!current) return;
              if (current.state === 'minimized') {
                kernel.wm.restore(win.id);
                kernel.wm.focus(win.id);
              } else if (current.id === kernel.wm.getState().focusedId) {
                kernel.wm.minimize(win.id);
              } else {
                kernel.wm.focus(win.id);
              }
            }}
          >
            <span className="house-bar-entry-label">{win.title}</span>
            {(badges.get(win.appId) ?? 0) > 0 && (
              <span
                className="house-bar-badge"
                data-testid={`bar-badge-${win.appId}`}
                aria-label={COPY.chrome.notifications}
              >
                {badges.get(win.appId)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* §6.3 — the house tray is one control: the clock, which opens §13's Center. */}
      <div className="house-bar-tray" data-testid="bar-tray">
        <button
          type="button"
          className="house-bar-clock"
          data-testid="bar-clock"
          aria-label={COPY.chrome.notifications}
          onClick={() => kernel.shellui.toggleOverlay('notifications')}
        >
          {time}
        </button>
      </div>
    </div>
  );
}
